import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Login from "./Login";
import Register from "./Register";
import { BASE_URL } from "../../config/api";

export default function Balance() {
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [balanceList, setBalanceList] = useState([]);

  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const [user, setUser] = useState(null);

  const [searchParams] = useSearchParams();
  const [paymentMessage, setPaymentMessage] = useState({
    type: "",
    title: "",
    text: "",
  });

  /*
  =========================
  Load user from localStorage
  =========================
  */

  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error(error);
      }
    }
  }, []);

  /*
  =========================
  Page title + wallet refresh
  =========================
  */

  useEffect(() => {
    document.title = "Balance & Passbook";

    if (!user) return;

    const userId = user.user_login_id || user.id;

    // Load latest wallet data
    fetchBalance(userId);
    fetchBalanceList(userId);

    // Check PayU return status
    const paymentStatus = searchParams.get("payment");

    if (paymentStatus === "success") {
      setPaymentMessage({
        type: "success",
        title: "Payment Successful!",
        text: "Thank you. Your transaction has been completed successfully and your wallet balance has been updated.",
      });
      fetchBalance(userId);
      fetchBalanceList(userId);
      window.dispatchEvent(new Event("balanceUpdated"));
    }

    if (paymentStatus === "failed") {
      setPaymentMessage({
        type: "failed",
        title: "Payment Failed!",
        text: "Unfortunately your payment could not be processed. Please try again.",
      });
    }
  }, [user, searchParams]);

  /*
  =========================
  Pagination
  =========================
  */

  const recordsPerPage = 50;

  const indexOfLastRecord = currentPage * recordsPerPage;

  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  const currentRecords = balanceList.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  const totalPages = Math.ceil(balanceList.length / recordsPerPage);

  /*
  =========================
  Fetch Current Balance
  =========================
  */

  const fetchBalance = async (userId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-balance.php?userid=${userId}`,
      );

      const json = await response.json();

      let parsed = json;

      if (json.d) {
        parsed = typeof json.d === "string" ? JSON.parse(json.d) : json.d;
      }

      setBalance(
        Number(parsed?.data?.currentbalance || parsed?.currentbalance || 0),
      );
    } catch (error) {
      console.error("Balance error:", error);
    }
  };

  /*
  =========================
  Fetch Passbook
  =========================
  */
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const fetchBalanceList = async (userId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/balance-list.php?userid=${userId}`,
      );

      const json = await response.json();

      let parsed = json;

      if (json.d) {
        parsed = typeof json.d === "string" ? JSON.parse(json.d) : json.d;
      }

      setBalanceList(parsed.data || []);
    } catch (error) {
      console.error("Passbook error:", error);
    }
  };
  /*
  =========================
  Add Wallet Balance - PayU
  =========================
  */

  const [loading, setLoading] = useState(false);

  const handleAddBalance = async () => {
    if (!user) {
      setShowLogin(true);
      return;
    }

    if (!amount || Number(amount) < 500) {
      alert("Please enter Minimum 500 Balance.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("userId", user.user_login_id);
      formData.append("amount", amount);

      const response = await fetch(`${BASE_URL}/create-payment.php`, {
        method: "POST",
        body: formData,
      });

      const text = await response.text();

      let data;

      try {
        data = JSON.parse(text);
      } catch (e) {
        console.log(text);
        throw new Error("Invalid response from server.");
      }

      if (data.success) {
        window.location.href = data.redirect;
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="container py-4">
      <h2>Balance & Passbook</h2>
      {paymentMessage.text && (
        <div className={`payment-card ${paymentMessage.type}`}>
          <div className="payment-icon">
            <i
              className={
                paymentMessage.type === "success"
                  ? "bi bi-check-circle-fill"
                  : "bi bi-exclamation-triangle-fill"
              }
            ></i>
          </div>

          <div className="payment-content">
            <h3>{paymentMessage.title}</h3>

            <p>{paymentMessage.text}</p>
          </div>

          <button
            className="close-payment"
            onClick={() =>
              setPaymentMessage({
                type: "",
                title: "",
                text: "",
              })
            }
          >
            ×
          </button>
        </div>
      )}
      {!user ? (
        <div className="card shadow-sm mt-4">
          <div className="card-body text-center py-5">
            <div className="fs-1 mb-3">🔒</div>

            <h4 className="text-danger">Please login first</h4>

            <p className="text-muted">
              You need to login to access your wallet balance and passbook.
            </p>

            <button
              className="btn btn-primary"
              onClick={() => setShowLogin(true)}
            >
              Login Now
            </button>
          </div>
        </div>
      ) : (
        <>
          <h4 className="mt-4">
            Wallet Balance : <span className="rupee-symbol">₹</span>
            {balance}
          </h4>

          {/* Add Balance */}

          <div className="card p-4 mt-3">
            <input
              type="number"
              min="500"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="form-control"
              placeholder="Enter Amount"
            />

            <small className="text-danger mt-2">
              Note: Add Min. ₹500 Balance in Wallet.
            </small>
            <button
              className="btn btn-primary mt-3"
              onClick={handleAddBalance}
              disabled={loading}
            >
              {loading ? "Please Wait..." : "Add Balance"}
            </button>
          </div>

          {/* Passbook */}

          <div className="card mt-4">
            <div className="card-header">
              <h5 className="mb-0">Passbook</h5>
            </div>

            <div className="card-body p-0">
              <table className="table table-striped table-bordered mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Description</th>
                    <th>Transaction</th>
                  </tr>
                </thead>

                <tbody>
                  {currentRecords.length > 0 ? (
                    currentRecords.map((item, index) => {
                      const [datePart, timePart] = (item.date || "").split(
                        " - ",
                      );

                      return (
                        <tr key={index}>
                          <td>{datePart}</td>
                          <td>{timePart}</td>
                          <td>{item.description}</td>

                          <td
                            className={
                              item.balance >= 0
                                ? "text-success fw-bold"
                                : "text-danger fw-bold"
                            }
                          >
                            {item.balance >= 0 ? "+" : "-"}{" "}
                            <span className="rupee-symbol">₹</span>
                            {""}
                            {Math.abs(item.balance)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="d-flex justify-content-center align-items-center p-3 gap-2">
                <button
                  className="btn btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage(currentPage - 1);
                    scrollToTop();
                  }}
                >
                  Previous
                </button>

                <span>
                  Page {currentPage} of {totalPages || 1}
                </span>

                <button
                  className="btn btn-secondary"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                    scrollToTop();
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <Login
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        openRegister={() => {
          setShowLogin(false);

          setShowRegister(true);
        }}
      />

      <Register
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        openLogin={() => {
          setShowRegister(false);

          setShowLogin(true);
        }}
      />
    </div>
  );
}
